import { useState, useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthorBox } from "@/components/insight/AuthorBox";
import { slugify } from "@/components/insight/InsightBody";
import { ArrowRight } from "lucide-react";
import {
  Crumbs,
  ReadingColumn,
  ReadingProgress,
  SectionIndex,
  SectionStrip,
} from "@/components/luminous/interior";

type Language = 'es' | 'en';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  category: string;
  tags?: string[];
  featured_image_url?: string;
  created_at: string;
  updated_at?: string;
  reading_time?: number;
  seo_title?: string;
  seo_description?: string;
}

const ARTICLE_ID = "blog-post-article";

/** Extrae el texto plano de los hijos de un nodo Markdown, para generar anclas estables. */
const flattenText = (node: unknown): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenText).join("");
  if (typeof node === "object" && "props" in (node as Record<string, unknown>)) {
    return flattenText((node as { props?: { children?: unknown } }).props?.children);
  }
  return "";
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { language, url, esUrl, enUrl, onLanguageChange } = usePageSeo();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) return;

      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (error) throw error;
        setBlogPost(data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return language === 'es'
      ? date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryLabel = (category: string) => {
    const categories = {
      es: {
        'ai-marketing': 'IA & Marketing',
        'seo': 'SEO',
        'google-ads': 'Google Ads',
        'social-media': 'Redes Sociales',
        'content-marketing': 'Marketing de Contenidos',
        'general': 'General'
      },
      en: {
        'ai-marketing': 'AI & Marketing',
        'seo': 'SEO',
        'google-ads': 'Google Ads',
        'social-media': 'Social Media',
        'content-marketing': 'Content Marketing',
        'general': 'General'
      }
    };
    return categories[language][category as keyof typeof categories[typeof language]] || category;
  };

  // Índice editorial real, extraído de los `##` del Markdown del CMS.
  const headings = useMemo(() => {
    if (!blogPost?.content) return [];
    const matches = [...blogPost.content.matchAll(/^##\s+(.+)$/gm)];
    return matches.map((m) => {
      const label = m[1].replace(/[*_`]/g, "").trim();
      return { id: slugify(label), label };
    });
  }, [blogPost?.content]);
  const showToc = headings.length >= 3;
  const tocLabel = language === "es" ? "Contenido" : "Contents";
  const blogPath = language === 'es' ? '/blog' : '/en/blog';

  if (loading) {
    return (
      <>
        <Header language={language} onLanguageChange={onLanguageChange} />
        <main className="min-h-screen flex items-center justify-center bg-[color:var(--v3-mineral)]">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[color:var(--v3-blue)] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="v3-note">{language === 'es' ? 'Cargando artículo...' : 'Loading article...'}</p>
          </div>
        </main>
        <Footer language={language} />
      </>
    );
  }

  if (error || !blogPost) {
    return <Navigate to="/404" replace />;
  }

  const pageTitle = blogPost.seo_title || blogPost.title;
  const pageDescription = blogPost.seo_description || blogPost.excerpt || blogPost.content.substring(0, 160);
  const blogUrl = url;

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{pageTitle} | Luis ROC</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={blogPost.tags?.join(', ')} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content={language === 'es' ? 'Spanish' : 'English'} />

        <link rel="canonical" href={blogUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="ROC Worldwide Agency LLC" />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        {blogPost.featured_image_url && (
          <>
            <meta property="og:image" content={blogPost.featured_image_url} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={blogPost.title} />
          </>
        )}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@luisrocmarketing" />
        <meta name="twitter:creator" content="@luisrocmarketing" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {blogPost.featured_image_url && (
          <meta name="twitter:image" content={blogPost.featured_image_url} />
        )}

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogPost.title,
            "description": pageDescription,
            "url": blogUrl,
            "datePublished": blogPost.created_at,
            "dateModified": blogPost.updated_at ?? blogPost.created_at,
            "author": { "@id": "https://luisroc.com/#luisroc" },
            "publisher": { "@id": "https://luisroc.com/#organization" },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": blogUrl
            },
            "articleSection": getCategoryLabel(blogPost.category),
            "keywords": blogPost.tags?.join(', '),
            ...(blogPost.featured_image_url && {
              "image": {
                "@type": "ImageObject",
                "url": blogPost.featured_image_url,
                "width": 1200,
                "height": 630
              }
            })
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[color:var(--v3-mineral)]">
        <Header language={language} onLanguageChange={onLanguageChange} />
        <ReadingProgress targetId={ARTICLE_ID} />

        <main>
          {/* Encabezado interior compacto */}
          <header className="border-b border-[color:var(--v3-line)] bg-[color:var(--v3-mineral)]">
            <div className="v3-shell pb-8 pt-8 lg:pb-10">
              <Crumbs
                items={[
                  { label: language === 'es' ? 'Blog' : 'Blog', to: blogPath },
                  { label: blogPost.title },
                ]}
              />
              <p className="v3-micro mt-6">
                {getCategoryLabel(blogPost.category)}
              </p>
              <h1
                className="v3-display mt-4 max-w-3xl"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.6rem)" }}
              >
                {blogPost.title}
              </h1>
              {blogPost.excerpt && (
                <p className="v3-body mt-5 max-w-2xl text-[1.05rem]">{blogPost.excerpt}</p>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[color:var(--v3-line)] pt-4 text-[0.85rem] text-[color:var(--v3-muted)]">
                <span>
                  {language === 'es' ? 'Por' : 'By'}{" "}
                  <Link to={language === 'es' ? '/sobre-mi' : '/en/about'} className="font-medium text-[color:var(--v3-ink)] underline-offset-4 hover:underline">
                    Luis ROC
                  </Link>
                </span>
                <time dateTime={blogPost.created_at}>
                  {language === 'es' ? 'Publicado' : 'Published'} {formatDate(blogPost.created_at)}
                </time>
                {blogPost.updated_at && blogPost.updated_at !== blogPost.created_at && (
                  <time dateTime={blogPost.updated_at}>
                    {language === 'es' ? 'Actualizado' : 'Updated'} {formatDate(blogPost.updated_at)}
                  </time>
                )}
                {blogPost.reading_time && (
                  <span>
                    {blogPost.reading_time} {language === 'es' ? 'min de lectura' : 'min read'}
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* Índice compacto en móvil */}
          {showToc && <SectionStrip items={headings} label={tocLabel} />}

          <div className="v3-shell v3-section-tight">
            <div className={showToc ? "grid gap-12 lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-16" : ""}>
              <article id={ARTICLE_ID} className="min-w-0">
                <ReadingColumn>
                  {blogPost.featured_image_url && (
                    <figure>
                      <img
                        src={blogPost.featured_image_url}
                        alt={blogPost.title}
                        className="w-full border border-[color:var(--v3-line)] object-cover"
                      />
                    </figure>
                  )}

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <h2 id={slugify(flattenText(children))} className="scroll-mt-28">
                          {children}
                        </h2>
                      ),
                      h2: ({ children }) => (
                        <h2 id={slugify(flattenText(children))} className="scroll-mt-28">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => <h3>{children}</h3>,
                      h4: ({ children }) => <h3>{children}</h3>,
                      table: ({ children }) => (
                        <div className="v3-table-wrap">
                          <table>{children}</table>
                        </div>
                      ),
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {blogPost.content}
                  </ReactMarkdown>

                  {blogPost.tags && blogPost.tags.length > 0 && (
                    <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 border-t border-[color:var(--v3-line)] pt-5">
                      {blogPost.tags.map((tag) => (
                        <li key={tag} className="v3-micro">{tag}</li>
                      ))}
                    </ul>
                  )}

                  {/* CTA contextual, al final */}
                  <div className="mt-14 border-l-2 border-[color:var(--v3-blue)] bg-[color:var(--v3-white)] p-6">
                    <p className="text-base leading-relaxed text-[color:var(--v3-ink)]">
                      {language === 'es'
                        ? '¿Necesitas ayuda con tu estrategia de marketing digital?'
                        : 'Need help with your digital marketing strategy?'}
                    </p>
                    <p className="v3-note mt-2">
                      {language === 'es'
                        ? 'Te ayudo a implementar estas estrategias en tu negocio y ver resultados reales.'
                        : 'I help you implement these strategies in your business and see real results.'}
                    </p>
                    <Link
                      to={language === 'es' ? '/contacto' : '/en/contact'}
                      className="v3-inline-link mt-4"
                    >
                      {language === 'es' ? 'Agendar reunión inicial gratuita' : 'Request a free initial consultation'}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>

                  <section className="mt-12 border-t border-[color:var(--v3-line)] pt-6">
                    <p className="v3-micro">
                      {language === 'es' ? 'Seguir leyendo' : 'Keep reading'}
                    </p>
                    <ul className="mt-4 space-y-2">
                      <li>
                        <Link to={blogPath} className="v3-inline-link">
                          {language === 'es' ? 'Volver al blog' : 'Back to the blog'}
                          <ArrowRight className="h-4 w-4" aria-hidden />
                        </Link>
                      </li>
                    </ul>
                  </section>

                  <AuthorBox language={language} />
                </ReadingColumn>
              </article>

              {showToc && (
                <aside className="hidden lg:block">
                  <div className="sticky top-24">
                    <SectionIndex items={headings} label={tocLabel} />
                  </div>
                </aside>
              )}
            </div>
          </div>
        </main>

        <Footer language={language} />
      </div>
    </>
  );
};

export default BlogPostPage;
